import type { OpinionRecord } from '~/models/rulesOpinion'
import type { OpinionId, WeeklyReviewId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { SolaDatabase } from '../db'
import { type StorageError, toStorageError } from '../errors'
import { STORE } from '../schema'

/**
 * Reads for persisted opinions.
 *
 * There is deliberately **no `putOpinion` here**. An opinion is only ever
 * written as part of finalizing its review, in one transaction, by
 * `finalizeWeeklyReview` — an opinion with no finished review behind it is an
 * orphan the export layer could still find, and a second write path is how that
 * happens. Keeping this module read-only makes the single writer structural
 * rather than a convention someone has to remember.
 */

export async function getOpinion(
  db: SolaDatabase,
  id: OpinionId,
): Promise<Result<OpinionRecord | undefined, StorageError>> {
  try {
    return ok(await db.get(STORE.opinions, id))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * The opinion attached to a review.
 *
 * One review has at most one opinion — finalizing is refused a second time — so
 * the first match is the answer rather than an arbitrary pick from many.
 */
export async function getOpinionForReview(
  db: SolaDatabase,
  reviewId: WeeklyReviewId,
): Promise<Result<OpinionRecord | undefined, StorageError>> {
  try {
    const rows = await db.getAllFromIndex(STORE.opinions, 'reviewId', reviewId)
    return ok(rows[0])
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/** Every opinion, oldest first — the `generatedAt` index gives ascending order. */
export async function listOpinions(
  db: SolaDatabase,
): Promise<Result<readonly OpinionRecord[], StorageError>> {
  try {
    return ok(await db.getAllFromIndex(STORE.opinions, 'generatedAt'))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}
